import { useShowToast } from '@/hooks';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef, useState, useCallback, useEffect } from 'react';
import { InterviewDetailsAPI } from '../api/InterviewDetailsAPI';
import { schProcessMeetingRoomChanges } from './schProcessMeetingRoomChanges';
import moment from 'moment-timezone';
import { isEqual } from 'lodash-es';

// Constants for timing and retries
const CACHE_TIMEOUT = 1 * 60 * 1000; // 1 minute cache timeout
const POLL_INTERVAL = 2000; // 2 seconds polling interval
const MAX_RETRIES = 5; // Max retries for polling

type PollMutationResponse = {
  data: {
    status: 'PENDING' | 'SUCCESS';
    results?: {
      room_events: Record<string, any[]>;
      rooms_info: Array<{
        room_email: string | number;
        calendar_available: boolean;
      }>;
    };
  };
  error?: any;
};

// Interface for interviewer data stored per week range
interface MeetingRoomData {
  availability?: { start: string; end: string }[]; // Available time slots
  busyEvents?: any[]; // Busy calendar events
  calenderConnected?: boolean; // Calendar connection status
  isZeroEvents?: boolean; // Indicates if no events exist
  cachedAt?: number; // Timestamp of last cache
}

/**
 * Custom hook to manage scheduling logic for meetingRooms with week-based data fetching.
 * Fetches current week data always, and next week data only on initial mount if getNextWeek is true.
 * Ensures data is fetched for the current week range on navigation if not already present.
 * @param timeZone - Time zone for scheduling (e.g., 'Asia/Kolkata')
 * @param meetingRoomIds - Array of meeting room IDs to fetch data for
 * @param currentWeekRange - Current week range identifier from FullCalendar (e.g., "Mar 2 – 8, 2025")
 * @param interviewDataRef - Ref object containing datesSet with startStr, endStr, and timeZone
 * @param jobId - Job ID for API calls
 * @param registerForUpdates - Function to register for state updates (e.g., meetingRooms, datesSet)
 * @param updateSharedData - Function to update shared data (e.g., roomCalendarInfo)
 * @param getNextWeek - Flag to fetch the next week only on initial mount alongside the current week
 * @param isBulkManual - Flag to indicate if the meeting is bulk manual
 * @param jobStageActivityId - Job stage activity ID
 * @returns Object containing current week data, full meetingRooms data, week range, clear cache function, and loading state
 */
export const useMeetingRoomScheduleLogic = ({
  meetingRoomIds = [],
  interviewDataRef,
  jobId,
  // currentWeekRange = '',
  registerForUpdates,
  updateSharedData,
  timeZone,
  getNextWeek = false,
  isBulkManual = false,
  jobStageActivityId,
}: {
  meetingRoomIds?: (string | number)[];
  interviewDataRef?: any;
  currentWeekRange?: string;
  jobId?: number;
  registerForUpdates: (key: string, callback: () => void) => () => void;
  updateSharedData: any;
  timeZone?: string;
  getNextWeek?: boolean;
  isBulkManual?: boolean;
  jobStageActivityId?: string | number;
}) => {
  // --- State and Refs ---
  const [, forceUpdate] = useState({}); // Dummy state to force re-renders on updates
  const [isPollingActive, setIsPollingActive] = useState(false); // Tracks if polling is active
  const [meetingRoomsData, setMeetingRoomsData] = useState<
    Record<string, Record<string, MeetingRoomData>>
  >({}); // Stores interviewer data by week range
  const [pollTrigger, setPollTrigger] = useState(0); // Triggers refetch on polling errors

  const toast = useShowToast(); // Toast notification utility

  // Refs for caching and tracking
  const tempCacheRef = useRef<Record<string, Record<string, MeetingRoomData>>>(
    {}
  ); // Temporary cache for interviewer data
  const timeoutIdsRef = useRef<Record<string, NodeJS.Timeout>>({}); // Timeout IDs for cache clearing
  const inProgressMeetingRoomsRef = useRef<Set<string | number>>(new Set()); // Tracks meetingRooms being fetched
  const initialFetchRef = useRef({ firstWeek: false, nextWeek: false }); // Tracks initial fetch completion

  // --- Refs and State for Bulk Manual Caching ---
  const initialCacheLoadAttemptedForStepRef = useRef<string | number | null>(
    null
  );
  const [
    cacheCheckCompletedForCurrentContext,
    setCacheCheckCompletedForCurrentContext,
  ] = useState(!isBulkManual);

  // --- Utility Functions ---
  /**
   * Clears cached data for a specific key or all keys
   * @param key - Optional specific key to clear; if omitted, clears all
   */
  const clearCache = useCallback((key?: string) => {
    if (key) {
      delete tempCacheRef.current[key];
      if (timeoutIdsRef.current[key]) {
        clearTimeout(timeoutIdsRef.current[key]);
        delete timeoutIdsRef.current[key];
      }
    } else {
      Object.values(timeoutIdsRef.current).forEach(clearTimeout);
      timeoutIdsRef.current = {};
      tempCacheRef.current = {};
    }
  }, []);

  // --- Event Listeners ---
  // Register for updates to meetingRooms and datesSet, forcing re-render on change
  useEffect(() => {
    const unregisterDatesSet = registerForUpdates('datesSet', () =>
      forceUpdate({})
    );
    const unregisterMeetingRooms = registerForUpdates('meetingRooms', () =>
      forceUpdate({})
    );

    const unregisterMeetingRoomObj = registerForUpdates('meetingRoomObj', () =>
      forceUpdate({})
    );

    return () => {
      unregisterDatesSet();
      unregisterMeetingRooms();
      unregisterMeetingRoomObj();
    };
  }, [registerForUpdates]);

  // --- Week Range Calculation ---
  const { datesSet, meetingRoomObj } = interviewDataRef?.current || {};

  // Current week range from FullCalendar's viewTitle
  const viewTitle = datesSet?.view?.getCurrentData()?.viewTitle || '';
  const currentWeekRange = viewTitle || ''; // e.g., "Mar 23 – 29, 2025"

  // Parse current week's start and end with timezone
  const currentWeekStart = datesSet?.startStr
    ? moment.tz(datesSet.startStr, timeZone)
    : null;

  // Get the original format of the current week start date from FullCalendar
  // const originalDateFormat = datesSet?.startStr || '';

  // Ensure we always have a correct week start (Sunday) and end (Saturday)
  const adjustedCurrentWeekStart = currentWeekStart
    ? currentWeekStart.clone().day(0) // Get the Sunday of the week
    : null;
  const adjustedCurrentWeekEnd = adjustedCurrentWeekStart
    ? adjustedCurrentWeekStart.clone().day(6) // Get the Saturday of the week
    : null;

  // Calculate next week's start (next Sunday) and end (next Saturday)
  const nextWeekStart = adjustedCurrentWeekStart
    ? adjustedCurrentWeekStart.clone().add(7, 'days') // Add 7 days to get next Sunday
    : null;
  const nextWeekEnd = adjustedCurrentWeekEnd
    ? adjustedCurrentWeekEnd.clone().add(7, 'days') // Add 7 days to get next Saturday
    : null;

  // Format nextWeekRange dynamically based on month and year transitions
  let nextWeekRange = '';
  if (nextWeekStart && nextWeekEnd) {
    if (nextWeekStart.year() === nextWeekEnd.year()) {
      if (nextWeekStart.month() === nextWeekEnd.month()) {
        // Same month, same year
        nextWeekRange = `${nextWeekStart.format(
          'MMM D'
        )} – ${nextWeekEnd.format('D, YYYY')}`;
      } else {
        // Different months, same year
        nextWeekRange = `${nextWeekStart.format(
          'MMM D'
        )} – ${nextWeekEnd.format('MMM D, YYYY')}`;
      }
    } else {
      // Different years (and implicitly different months)
      nextWeekRange = `${nextWeekStart.format(
        'MMM D, YYYY'
      )} – ${nextWeekEnd.format('MMM D, YYYY')}`;
    }
  }

  // --- Effect to reset cache flags on context change for bulk manual mode ---
  useEffect(() => {
    if (isBulkManual) {
      setCacheCheckCompletedForCurrentContext(false);
      // If the step (jobStageActivityId) changes, we need to allow a fresh cache load attempt for the new step.
      if (
        jobStageActivityId && // Ensure jobStageActivityId is defined
        initialCacheLoadAttemptedForStepRef.current !== jobStageActivityId
      ) {
        initialCacheLoadAttemptedForStepRef.current = null;
      }
    } else {
      // If not in bulk mode, cache check is effectively always "complete" from a fetch-enabling perspective,
      // and the step-specific ref is not used/should be reset.
      setCacheCheckCompletedForCurrentContext(true);
      initialCacheLoadAttemptedForStepRef.current = null;
    }
  }, [isBulkManual, jobStageActivityId, currentWeekRange, meetingRoomIds]);

  // --- Effect to load initial meetingRoomsData from shared cache in bulk manual mode ---
  useEffect(() => {
    // Only proceed if in bulk manual mode and critical data for cache lookup is available.
    if (
      !isBulkManual ||
      !jobStageActivityId ||
      !currentWeekRange || // Ensure currentWeekRange is stable and defined
      meetingRoomIds.length === 0
    ) {
      // If not ready to attempt cache load, ensure cache check completion is false
      // (unless it was explicitly set true elsewhere for a non-bulk scenario).
      // This prevents premature fetch enabling if context isn't stable yet.
      if (isBulkManual && cacheCheckCompletedForCurrentContext) {
        // setCacheCheckCompletedForCurrentContext(false); // Revisit if needed, might cause loops
      }
      return;
    }

    // If we've already attempted to load cache for this specific step ID,
    // OR if the cache check has already completed for this exact context, don't re-process.
    if (initialCacheLoadAttemptedForStepRef.current === jobStageActivityId) {
      // If already attempted for this step, ensure completion flag is true if it wasn't already.
      if (!cacheCheckCompletedForCurrentContext) {
        setCacheCheckCompletedForCurrentContext(true);
      }
      return;
    }

    const sharedRoomEventsInfo = (interviewDataRef?.current as any)
      ?.roomEventsInfo as
      | Record<string, Record<string, MeetingRoomData>> // Using MeetingRoomData type here
      | undefined;

    if (sharedRoomEventsInfo) {
      const dataForCurrentWeekFromShared =
        sharedRoomEventsInfo[currentWeekRange];

      if (
        dataForCurrentWeekFromShared &&
        Object.keys(dataForCurrentWeekFromShared).length > 0
      ) {
        // Check if the shared cache has data for *all* currently selected meeting rooms for this week.
        const hasDataForAllSelectedRoomsInShared = meetingRoomIds.every((id) =>
          Object.prototype.hasOwnProperty.call(
            dataForCurrentWeekFromShared,
            String(id) // Assuming meetingRoomIds are string or number
          )
        );

        if (hasDataForAllSelectedRoomsInShared) {
          const dataToUseForWeek = { ...dataForCurrentWeekFromShared }; // Create a copy
          const currentLocalDataForWeek =
            meetingRoomsData[currentWeekRange] || {};

          if (!isEqual(currentLocalDataForWeek, dataToUseForWeek)) {
            setMeetingRoomsData((prevData) => ({
              ...prevData,
              [currentWeekRange!]: dataToUseForWeek, // Use the copy
            }));
            // No direct 'markAsFetched' for rooms like interviewers yet,
            // fetch disabling will rely on cacheCheckCompletedForCurrentContext.
          }
        }
      }
    }

    // Mark that an attempt to load from cache for this step has been made.
    initialCacheLoadAttemptedForStepRef.current = jobStageActivityId;
    // CRITICAL: Mark that the cache check for THIS current, stable context has completed.
    // This allows subsequent logic (like fetch enabling) to proceed.
    setCacheCheckCompletedForCurrentContext(true);
  }, [
    isBulkManual,
    jobStageActivityId,
    currentWeekRange,
    meetingRoomIds,
    interviewDataRef,
    meetingRoomsData, // For isEqual comparison
    setMeetingRoomsData,
    cacheCheckCompletedForCurrentContext, // To ensure it only runs once after context is stable
    // isEqual is used; imported from lodash-es/isEqual.
  ]);

  // --- Effect to update shared roomEventsInfo with local meetingRoomsData if isBulkManual ---
  useEffect(() => {
    if (isBulkManual) {
      const currentSharedRoomEventsInfo = (interviewDataRef?.current as any)
        ?.roomEventsInfo;

      // Only update if there's actual data and it's different from the shared cache.
      // This prevents unnecessary updates if meetingRoomsData is empty or unchanged.
      if (
        Object.keys(meetingRoomsData).length > 0 &&
        !isEqual(currentSharedRoomEventsInfo, meetingRoomsData)
      ) {
        updateSharedData({
          roomEventsInfo: { ...meetingRoomsData }, // Send a copy
        });
      }
    }
  }, [isBulkManual, meetingRoomsData, updateSharedData, interviewDataRef]);

  // --- Interviewer Processing ---
  const { toFetch: meetingRoomsToFetch } = schProcessMeetingRoomChanges({
    currentWeekRange, //current week range
    meetingRoomsData, // meetingRooms data
    meetingRoomIds, // meeting room ids
    tempCacheRef, // remove id stored in cache to clear after timeout
    timeoutIdsRef, // timeout ids ref
    inProgressMeetingRoomsRef, // race condition to fetch meetingRooms data if the previous fetch is not completed [1] --> [1, 2]
    clearCache, // clear cache
    setMeetingRoomsData, // set meetingRooms data
    CACHE_TIMEOUT,
  });

  const isWeekRangeValid = (weekRange: string, startStr: string) => {
    if (!weekRange || !startStr) return false;

    // Parse the start date from datesSet?.startStr
    const startDate = moment.tz(startStr, timeZone);

    // Parse the start date from currentWeekRange
    const [startPart, endPart] = weekRange.split(' – ');

    // Handle different formats for the start part
    let month, day, year;

    // Check if the start part includes the year (e.g., "Dec 28, 2025")
    if (startPart.includes(',')) {
      // Format: "Dec 28, 2025"
      [month, day] = startPart.split(' ').slice(0, 2);
      year = startPart.split(', ')[1];
    } else {
      // Format: "Dec 28" (year is in the end part)
      [month, day] = startPart.split(' ');
      // Extract year from the end part
      const [endDay, endYear] = endPart.split(', ');
      year = endYear;
      // If end part includes a month (e.g., "Jan 3, 2026"), we need to check if the start month/year differs
      if (endDay.includes(' ')) {
        const [endMonth] = endDay.split(' ');
        // If months differ and the start month is December, the start year is the previous year
        if (month === 'Dec' && endMonth === 'Jan') {
          year = String(parseInt(endYear) - 1);
        }
      }
    }

    const weekRangeStart = moment.tz(
      `${month} ${day}, ${year}`,
      'MMM D, YYYY',
      timeZone
    );

    // Check if the dates are in the same week
    return startDate.isSame(weekRangeStart, 'week');
  };

  const isCurrentWeekValid = isWeekRangeValid(
    currentWeekRange,
    datesSet?.startStr
  );

  const shouldEnableFetchFromCachePerspective = isBulkManual
    ? cacheCheckCompletedForCurrentContext
    : true;

  /**
   * Fetches calendar event IDs and initiates polling for a week range
   * @param weekRange - Identifier for the week
   * @param from - Start date for the fetch
   * @param to - End date for the fetch
   * @returns API response or empty object on failure
   */
  const fetchCalendarEvents = async (
    weekRange: string,
    from: string,
    to: string
  ) => {
    try {
      const response = await InterviewDetailsAPI.getCalenderEventIds({
        from,
        to,
        time_zone: timeZone,
        user_ids: [],
        room_emails: meetingRoomsToFetch,
        ats_job_id: jobId,
      });

      if (!response?.fetch_uuid) {
        return {};
      }

      if (!isPollingActive) {
        const meetingRoomsAtFetch = [...meetingRoomsToFetch]; // Capture meetingRooms at fetch time
        if (weekRange === currentWeekRange) {
          currentPollMutation.mutate({
            fetch_uuid: response.fetch_uuid,
            weekRange,
            meetingRooms: meetingRoomsAtFetch,
          });
        } else {
          nextPollMutation.mutate({
            fetch_uuid: response.fetch_uuid,
            weekRange,
            meetingRooms: meetingRoomsAtFetch,
          });
        }
      }

      return response;
    } catch (error) {
      console.error(`Error fetching calendar events for ${weekRange}:`, error);
      return {};
    }
  };

  // Fetch current week calendar events (always enabled if conditions met)
  const { isFetching: isCurrentEventIdFetching } = useQuery({
    queryKey: [
      'getCalenderEventIds',
      meetingRoomsToFetch,
      currentWeekRange,
      jobId,
      pollTrigger,
    ],
    queryFn: () =>
      fetchCalendarEvents(
        currentWeekRange,
        datesSet?.startStr,
        datesSet?.endStr
      ),
    enabled:
      shouldEnableFetchFromCachePerspective &&
      meetingRoomsToFetch.length > 0 &&
      !!datesSet?.startStr &&
      timeZone !== 'local' &&
      isCurrentWeekValid,
  });

  // Fetch next week calendar events only on initial mount if getNextWeek is true and not yet fetched
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isFetching: isNextEventIdFetching } = useQuery({
    queryKey: [
      'getCalenderEventIds',
      meetingRoomsToFetch,
      nextWeekRange,
      jobId,
      pollTrigger,
    ],
    queryFn: () =>
      fetchCalendarEvents(
        nextWeekRange,
        nextWeekStart.toISOString(),
        nextWeekEnd.toISOString()
      ),
    enabled:
      shouldEnableFetchFromCachePerspective &&
      getNextWeek &&
      !initialFetchRef.current.nextWeek && // Only fetch if not already done
      meetingRoomsToFetch.length > 0 &&
      !!nextWeekStart &&
      timeZone !== 'local',
  });

  // --- Polling Mutations ---
  // Poll for current week events
  const currentPollMutation = useMutation<
    PollMutationResponse,
    Error,
    { fetch_uuid: string; weekRange: string; meetingRooms: (string | number)[] }
  >({
    // @ts-ignore
    mutationFn: async (variables) => {
      const response = await InterviewDetailsAPI.getPollCalenderEvents({
        eventId: variables.fetch_uuid,
        jobId,
      });
      return response;
    },
    retry: MAX_RETRIES,
    onMutate: () => setIsPollingActive(true),
    onSuccess: (response, variables) => {
      if (response?.data?.status === 'PENDING') {
        setTimeout(
          () =>
            currentPollMutation.mutate({
              fetch_uuid: variables.fetch_uuid,
              weekRange: variables.weekRange,
              meetingRooms: variables.meetingRooms,
            }),
          POLL_INTERVAL
        );
        return;
      }

      if (response?.data?.status === 'SUCCESS') {
        const { room_events = {}, rooms_info = [] } =
          response?.data?.results || {};
        const roomCalendarInfo = {
          ...interviewDataRef?.current?.roomCalendarInfo,
        };
        const meetingRoomInfo = {};

        rooms_info.forEach((r) => {
          const roomIdStr = String(r?.room_email);
          const eventList = room_events[roomIdStr] || [];
          meetingRoomInfo[roomIdStr] = { ...r };
          roomCalendarInfo[roomIdStr] = {
            ...r,
            isCalendarConnected: Boolean(r?.calendar_available),
            zeroEventsByRange: {
              ...(roomCalendarInfo[roomIdStr]?.zeroEventsByRange || {}),
              [variables.weekRange]: eventList.length === 0,
            },
          };
        });

        updateSharedData({ roomCalendarInfo });

        if (!variables.weekRange) return;

        setMeetingRoomsData((prev) => {
          const updatedData = {
            ...prev,
            [variables.weekRange]: {
              ...prev[variables.weekRange],
              ...variables.meetingRooms.reduce((acc, id) => {
                const roomIdStr = String(id);
                const eventList = room_events[roomIdStr] || [];
                acc[roomIdStr] = {
                  ...(prev[variables.weekRange]?.[roomIdStr] || {}),
                  availability: [],
                  busyEvents: eventList.map((e) => ({
                    ...e,
                    start_time: e.start_time || e.start,
                    end_time: e.end_time || e.end,
                    meetingRoomInfo: meetingRoomInfo[roomIdStr],
                    subject: meetingRoomObj[roomIdStr],
                    eventType: 'meeting_room',
                  })),
                  eventType: 'meeting_room',
                  calenderConnected: Boolean(
                    meetingRoomInfo[roomIdStr]?.calendar_available
                  ),
                  isZeroEvents: eventList.length === 0,
                };
                inProgressMeetingRoomsRef.current.delete(id);
                return acc;
              }, {}),
            },
          };

          return updatedData;
        });

        setIsPollingActive(false);
      }
    },
    onError: (error, variables) => {
      setIsPollingActive(false);
      variables.meetingRooms.forEach((id) =>
        inProgressMeetingRoomsRef.current.delete(id)
      );
      toast({
        title: 'Failed to fetch calendar events',
        description: error.message,
        status: 'error',
      });
      setPollTrigger((prev) => prev + 1);
    },
    onSettled: (response, error, variables) => {
      if (response?.data?.status !== 'PENDING') {
        setIsPollingActive(false);
      }
    },
  });

  // Poll for next week events (only triggered on initial mount if getNextWeek is true)
  const nextPollMutation = useMutation<
    PollMutationResponse,
    Error,
    { fetch_uuid: string; weekRange: string; meetingRooms: (string | number)[] }
  >({
    // @ts-ignore
    mutationFn: async (variables) => {
      const response = await InterviewDetailsAPI.getPollCalenderEvents({
        eventId: variables.fetch_uuid,
        jobId,
      });
      return response;
    },
    retry: MAX_RETRIES,
    // onMutate: () => setIsPollingActive(true),
    onSuccess: (response, variables) => {
      if (response?.data?.status === 'PENDING') {
        setTimeout(
          () =>
            nextPollMutation.mutate({
              fetch_uuid: variables.fetch_uuid,
              weekRange: variables.weekRange,
              meetingRooms: variables.meetingRooms,
            }),
          POLL_INTERVAL
        );
        return;
      }

      if (response?.data?.status === 'SUCCESS') {
        const { room_events = {}, rooms_info = [] } =
          response?.data?.results || {};
        const roomCalendarInfo = {};
        const meetingRoomInfo = {};

        rooms_info.forEach((r) => {
          const meetingIdStr = String(r?.room_email);
          const eventList = room_events[meetingIdStr] || [];
          meetingRoomInfo[meetingIdStr] = { ...r };
          roomCalendarInfo[meetingIdStr] = {
            isCalendarConnected: Boolean(r?.calendar_available),
            zeroEventsByRange: {
              ...(roomCalendarInfo[meetingIdStr]?.zeroEventsByRange || {}),
              [variables.weekRange]: eventList.length === 0,
            },
          };
        });

        // updateSharedData({ roomCalendarInfo });

        if (!variables.weekRange) return;

        setMeetingRoomsData((prev) => {
          const updatedData = {
            ...prev,
            [variables.weekRange]: {
              ...prev[variables.weekRange],
              ...variables.meetingRooms.reduce((acc, id) => {
                const meetingIdStr = String(id);
                const eventList = room_events[meetingIdStr] || [];
                acc[meetingIdStr] = {
                  ...(prev[variables.weekRange]?.[meetingIdStr] || {}),
                  availability: [],
                  busyEvents: eventList.map((e) => ({
                    ...e,
                    // Normalize start_time/end_time for providers (e.g. mscal) that may return start/end
                    start_time: e.start_time || e.start,
                    end_time: e.end_time || e.end,
                    meetingRoomInfo: meetingRoomInfo[meetingIdStr],
                    subject: meetingRoomObj[meetingIdStr],
                    eventType: 'meeting_room',
                  })),
                  calenderConnected: Boolean(
                    roomCalendarInfo[meetingIdStr]?.isCalendarConnected
                  ),
                  isZeroEvents: eventList.length === 0,
                  eventType: 'meeting_room',
                };
                inProgressMeetingRoomsRef.current.delete(id);
                return acc;
              }, {}),
            },
          };
          return updatedData;
        });

        // setIsPollingActive(false);
      }
    },
    onError: (error, variables) => {
      console.error(`Poll error for ${variables.weekRange}:`, error);
      // setIsPollingActive(false);
      variables.meetingRooms.forEach((id) =>
        inProgressMeetingRoomsRef.current.delete(id)
      );
      toast({
        title: 'Failed to fetch calendar events',
        description: error.message,
        status: 'error',
      });
      setPollTrigger((prev) => prev + 1);
    },
    onSettled: (response, error, variables) => {
      if (response?.data?.status !== 'PENDING') {
        // setIsPollingActive(false);
      }
    },
  });

  // --- Loading State ---
  // Aggregate loading states from all queries and polling
  const isLoading =
    // isCurrentAvailabilityLoading ||
    // isNextAvailabilityLoading ||
    isCurrentEventIdFetching ||
    // isNextEventIdFetching ||
    isPollingActive;

  // --- Return Values ---
  return {
    currentWeekData: meetingRoomsData?.[currentWeekRange] || {}, // Data for the current week
    currentWeekRange, // Current week range identifier
    meetingRoomsData, // Full data across all fetched weeks
    clearCache, // Function to clear cache
    isLoading, // Loading state for UI feedback
  };
};
